import { Constants } from "appConstants";
import { Input, SelectForm } from "components";
import { MaskHelper } from "helpers";
import { assignFieldValues, FieldSetter, useBlurHandler, useChangeHandler, usePrefixedSetter } from "helpers/FieldHandlerHelper";
import { useCallback, useState } from "react";
import AddressService from "services/addressService";
import { AddressDataResponse } from "services/types";
import { applicationDataSections } from ".";
import { Section, SectionContent, SectionHeader, SectionItem } from "../layout";
import { ApplicantDataFields, ApplicationFields, ApplicationFormik } from "../types";

const applicantDataPrefix = ApplicationFields.applicantData;

export default function ApplicantAddressSection({ formik }: Readonly<ApplicationFormik>): JSX.Element {
	const { applicantData: values } = formik.values;
	const errors = formik.errors?.applicantData;
	const touched = formik.touched?.applicantData;

	const setApplicationFieldValue = usePrefixedSetter(applicantDataPrefix, formik.setFieldValue);
	const handleChange = useChangeHandler(setApplicationFieldValue);

	const setApplicationFieldTouched = usePrefixedSetter(applicantDataPrefix, formik.setFieldTouched);
	const handleBlur = useBlurHandler(setApplicationFieldTouched);

	const [loadingAddressData, setLoadingAddressData] = useState<boolean>(false);
	const applyAddressData = useCallback(
		(data: AddressDataResponse) => {
			const { logradouro, localidade, bairro, uf, erro } = data;
			if (erro) {
				const fullFieldName = `${applicantDataPrefix}.${ApplicantDataFields.applicantAddressPostalCode}`;
				formik.setFieldTouched(fullFieldName, true, false);
				formik.setFieldError(fullFieldName, Constants.ErrorMessages.postalCodeApiError);
				return;
			}

			const applicantAddressData = {
				[ApplicantDataFields.applicantAddressStreet]: logradouro,
				[ApplicantDataFields.applicantAddressCity]: localidade,
				[ApplicantDataFields.applicantAddressNeighbourhood]: bairro,
				[ApplicantDataFields.applicantAddressState]: uf
			};
			assignFieldValues(applicantAddressData, setApplicationFieldValue);
		},
		[setApplicationFieldValue]
	);

	const onChangePostalCode = (evt: React.ChangeEvent<HTMLInputElement>) => {
		handleChange(evt, MaskHelper.cep);
		const addressPostalCode = evt.target.value;
		const shouldCallPostalCodeApi = addressPostalCode && addressPostalCode.length === "00000-000".length;

		if (shouldCallPostalCodeApi) {
			setLoadingAddressData(true);
			AddressService.getAddress(addressPostalCode)
				.then(applyAddressData)
				.catch(() => {
					const fullFieldName = `${applicantDataPrefix}.${ApplicantDataFields.applicantAddressPostalCode}`;
					formik.setFieldTouched(fullFieldName, true, false);
					formik.setFieldError(fullFieldName, Constants.ErrorMessages.postalCodeApiError);
				})
				.finally(() => setLoadingAddressData(false));
		}
	};

	return (
		<Section>
			<SectionHeader title="Informações complementares" />
			<SectionContent id={`section-${applicationDataSections.applicantAddress}`}>
				<SectionItem colSpan={3}>
					<Input
						type="text"
						name={ApplicantDataFields.applicantAddressPostalCode}
						label="CEP"
						placeholder="Digite"
						value={values.applicantAddressPostalCode}
						error={errors?.applicantAddressPostalCode}
						touched={touched?.applicantAddressPostalCode}
						onChange={onChangePostalCode}
						onBlur={handleBlur}
					/>
				</SectionItem>
				<SectionItem colSpan={9}>
					<Input
						type="text"
						name={ApplicantDataFields.applicantAddressStreet}
						label="Endereço"
						placeholder="Digite"
						value={values.applicantAddressStreet}
						error={errors?.applicantAddressStreet}
						touched={touched?.applicantAddressStreet}
						onChange={handleChange}
						onBlur={handleBlur}
						disabled={loadingAddressData}
					/>
				</SectionItem>
				<SectionItem colSpan={4}>
					<Input
						type="text"
						name={ApplicantDataFields.applicantAddressNumber}
						label="Número"
						placeholder="Digite"
						value={values.applicantAddressNumber}
						error={errors?.applicantAddressNumber}
						touched={touched?.applicantAddressNumber}
						onChange={handleChange}
						onBlur={handleBlur}
					/>
				</SectionItem>
				<SectionItem colSpan={4}>
					<Input
						type="text"
						name={ApplicantDataFields.applicantAddressComplement}
						label="Complemento"
						placeholder="Digite"
						value={values.applicantAddressComplement}
						error={errors?.applicantAddressComplement}
						touched={touched?.applicantAddressComplement}
						onChange={handleChange}
						onBlur={handleBlur}
						required={false}
						showOptionalIndicator={false}
					/>
				</SectionItem>
				<SectionItem colSpan={4}>
					<Input
						type="text"
						name={ApplicantDataFields.applicantAddressNeighbourhood}
						label="Bairro"
						placeholder="Digite"
						value={values.applicantAddressNeighbourhood}
						error={errors?.applicantAddressNeighbourhood}
						touched={touched?.applicantAddressNeighbourhood}
						onChange={handleChange}
						onBlur={handleBlur}
						disabled={loadingAddressData}
					/>
				</SectionItem>
				<SectionItem colSpan={4}>
					<SelectForm
						name={ApplicantDataFields.applicantAddressState}
						label="UF"
						placeholder="Selecione"
						value={values.applicantAddressState}
						error={errors?.applicantAddressState}
						touched={touched?.applicantAddressState}
						isDisabled={loadingAddressData}
						options={Constants.federalUnitCodeOptions}
						getOptionValue={(option) => option.value}
						setFieldValue={setApplicationFieldValue as FieldSetter<string>}
						className="!mt-0"
					/>
				</SectionItem>
				<SectionItem colSpan={8}>
					<Input
						type="text"
						name={ApplicantDataFields.applicantAddressCity}
						label="Cidade"
						placeholder="Digite"
						value={values.applicantAddressCity}
						error={errors?.applicantAddressCity}
						touched={touched?.applicantAddressCity}
						onChange={handleChange}
						onBlur={handleBlur}
						disabled={loadingAddressData}
					/>
				</SectionItem>
			</SectionContent>
		</Section>
	);
}
